// Tracked (finger-following) swipe helper shared by the side panel and the
// fullscreen viewer. Both surfaces already own their pointer event wiring
// (capture, multi-pointer pinch, tap/double-tap disambiguation) — this module
// doesn't attach any listeners itself, it just turns a sequence of positions
// fed in by the caller into a live dx/dy (for driving a CSS transform while
// the finger is down) and a commit-vs-cancel decision at release.
//
// (One deliberate exception to "no listeners, no DOM": cancelPendingDrag()
// below dispatches a single synthetic event — see its comment.)
//
// Commit rule: the dominant axis (whichever of |dx|/|dy| is larger) wins, and
// it commits if that axis moved past COMMIT_DIST OR the trailing velocity on
// that axis was fast enough to count as a fling (a short flick that hasn't
// travelled far yet). Otherwise it cancels — caller springs back.
//
// `axisLock` (opt-in): for a caller like the viewer that drags
// freely on both screen axes, `move()` zeros out whichever axis isn't
// currently dominant (re-decided on every call — while the gesture is still
// small this can flip, so an early wobble doesn't lock in the wrong axis),
// and `end()` additionally rejects a diagonal release outright (minor axis
// too close to the dominant one) even past COMMIT_DIST/FLING_VELOCITY. The
// side panel only ever moves along x and doesn't opt in — its own dragged-flag
// check (preview.js) already ignores dy.

const COMMIT_DIST = 60;       // px
const FLING_VELOCITY = 0.5;   // px/ms (~500px/s)
const VELOCITY_WINDOW = 6;    // trailing samples used for the velocity estimate
const AXIS_LOCK_RATIO = 2;    // axisLock: dominant axis must be >= this many times the minor axis

// A double-tap needs the two taps close together in space AND time — "same
// slot/cell" alone is far too loose. 200ms matches the usual platform
// double-tap timeout. Only createTapDetector below consumes these.
const DBL_TAP_MS = globalThis.__MS_TEST_TIMINGS__?.dblTapMs ?? 200;
const DBL_TAP_DIST = 40;

// Cancel whatever delayed/chosen drag SortableJS has armed for the current
// pointer by dispatching a synthetic pointercancel on the document. Sortable
// binds its cancellers there without checking pointerId: before the delay
// elapses this hits `_disableDelayedDrag`, after it hits `_onDrop` — neither
// dispatches `end` (gated on Sortable.active), so no reorder/history/save.
// Used by layout.js's pinch guard (a second finger down) and selection.js's
// long-press (entering range-select). The synthetic event carries pointerId 0,
// so a caller tracking a real finger can tell its own cancel apart from a
// browser one by pointerId.
export function cancelPendingDrag() {
  document.dispatchEvent(new PointerEvent('pointercancel', { bubbles: true }));
}

export function createTapDetector() {
  let tapT = 0, tapKey = null, tapX = 0, tapY = 0;
  return function(e, key) {
    if (key === null) {
      tapKey = null;
      return 'reset';
    }
    const now = Date.now();
    const isDbl = key === tapKey && now - tapT < DBL_TAP_MS &&
      Math.hypot(e.clientX - tapX, e.clientY - tapY) < DBL_TAP_DIST;
    if (isDbl) {
      tapT = 0; tapKey = null;
      return 'double';
    } else {
      tapT = now; tapKey = key; tapX = e.clientX; tapY = e.clientY;
      return 'single';
    }
  };
}

export function createSwipeTracker({ axisLock = false } = {}) {
  let x0 = 0, y0 = 0, samples = [];
  return {
    start(x, y) {
      x0 = x; y0 = y;
      samples = [{ x, y, t: performance.now() }];
    },
    // Live delta since the gesture started — apply this as a transform.
    move(x, y) {
      samples.push({ x, y, t: performance.now() });
      if (samples.length > VELOCITY_WINDOW) samples.shift();
      let dx = x - x0, dy = y - y0;
      if (axisLock) { if (Math.abs(dx) >= Math.abs(dy)) dy = 0; else dx = 0; }
      return { dx, dy };
    },
    // Decide commit vs cancel at release.
    // commit === false: caller should spring back to the resting position.
    // commit === true: `axis` ('x'|'y') is the dominant axis and `sign` is
    // its direction (>0 = dragged right/down, <0 = dragged left/up) — by
    // convention across both callers, sign > 0 steps to the previous item
    // and sign < 0 steps to the next (matches "content follows the finger":
    // dragging right/down reveals what came before).
    end(x, y) {
      const dx = x - x0, dy = y - y0;
      const first = samples[0], last = samples[samples.length - 1];
      const dt = Math.max(1, last.t - first.t);
      const vx = (last.x - first.x) / dt, vy = (last.y - first.y) / dt;
      const adx = Math.abs(dx), ady = Math.abs(dy);
      const horizontal = adx >= ady;
      const axisDist = horizontal ? adx : ady;
      const minorDist = horizontal ? ady : adx;
      const axisVel = horizontal ? Math.abs(vx) : Math.abs(vy);
      if (axisLock && minorDist * AXIS_LOCK_RATIO > axisDist) return { commit: false, dx, dy };
      if (axisDist < COMMIT_DIST && axisVel < FLING_VELOCITY) return { commit: false, dx, dy };
      const sign = Math.sign(horizontal ? dx : dy) || 1;
      return { commit: true, axis: horizontal ? 'x' : 'y', sign, dx, dy };
    },
  };
}

// Drive a one-shot CSS transition on `el`: set `transitionCss`, run `mutate()`
// to change the transitioning property, then call `onDone` exactly once —
// on `transitionend`, or after `fallbackMs` if it never fires (e.g. the
// mutated value turns out equal to what it already was). Both the side panel's
// commit-step/spring-back and the viewer's swipe commit/spring-back need this
// same "animate, then run a callback" plumbing, so it lives here once instead
// of as a hand-rolled transitionend listener in each caller.
export function runTransition(el, transitionCss, mutate, onDone, fallbackMs = 200) {
  if (!el) { onDone?.(); return; }
  el.style.transition = transitionCss;
  mutate();
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    el.removeEventListener('transitionend', finish);
    onDone?.();
  };
  el.addEventListener('transitionend', finish, { once: true });
  setTimeout(finish, fallbackMs);
}
